// src/pages/portal/marketing/PromotionsTab.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    promotionsService,
    categoryService,
    productService,
    diningOptionService,
} from '../../../services/api';
import { useLanguage } from '../../../context/LanguageContext';
import useIsMobile from '../../../hooks/useIsMobile';
import { C, FONT } from '../../../styles/theme';

// ─── Constants ────────────────────────────────────────────────────────────────
const RULE_TYPES = [
    'order_percentage',
    'order_fixed',
    'item_percentage',
    'item_fixed',
    'bogo',
    'combo',
];

const DAYS = [
    { value: 1, key: 'day_mon' },
    { value: 2, key: 'day_tue' },
    { value: 3, key: 'day_wed' },
    { value: 4, key: 'day_thu' },
    { value: 5, key: 'day_fri' },
    { value: 6, key: 'day_sat' },
    { value: 7, key: 'day_sun' },
];

const EMPTY_FORM = {
    name: '',
    description: '',
    rule_type: 'order_percentage',
    rule_config: {},
    scope_type: 'all',
    scope_ids: [],
    min_order_value: '',
    dining_option: '',
    schedule_type: 'always',
    start_date: '',
    end_date: '',
    days_of_week: [],
    start_time: '',
    end_time: '',
    application_mode: 'automatic',
    enabled: true,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const ruleTypeLabel = (rule_type, t) => {
    const map = {
        order_percentage: t('rule_order_percentage'),
        order_fixed:      t('rule_order_fixed'),
        item_percentage:  t('rule_item_percentage'),
        item_fixed:       t('rule_item_fixed'),
        bogo:             t('rule_bogo'),
        combo:            t('rule_combo'),
    };
    return map[rule_type] || rule_type;
};

const scheduleLabel = (promo, t) => {
    if (promo.schedule_type === 'date_range') {
        const s = promo.start_date ? String(promo.start_date).slice(0, 10) : '';
        const e = promo.end_date   ? String(promo.end_date).slice(0, 10)   : '';
        return s && e ? `${s} – ${e}` : s || e || t('schedule_date_range');
    }
    if (promo.schedule_type === 'recurring') {
        const dayNums = Array.isArray(promo.days_of_week) ? promo.days_of_week : [];
        const dayLabels = DAYS.filter(d => dayNums.includes(d.value)).map(d => t(d.key));
        const times = promo.start_time && promo.end_time
            ? ` ${promo.start_time}–${promo.end_time}`
            : '';
        return dayLabels.join(', ') + times || t('schedule_recurring');
    }
    return t('always_active');
};

// ─── API payload helper ───────────────────────────────────────────────────────
const toApiPayload = (promo, overrides = {}) => ({
    name:             promo.name,
    description:      promo.description ?? null,
    enabled:          promo.enabled,
    application_mode: promo.application_mode,
    rule_type:        promo.rule_type,
    rule_config:      promo.rule_config || {},
    min_order_value:  promo.min_order_value != null ? promo.min_order_value : null,
    dining_option:    promo.dining_option || null,
    scope_type:       promo.scope_type,
    scope_ids:        promo.scope_ids ?? null,
    schedule_type:    promo.schedule_type,
    start_date:       promo.start_date ?? null,
    end_date:         promo.end_date ?? null,
    days_of_week:     promo.days_of_week ?? null,
    start_time:       promo.start_time ?? null,
    end_time:         promo.end_time ?? null,
    ...overrides,
});

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onDismiss }) => (
    <div
        onClick={onDismiss}
        style={{
            padding: '10px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
            background: type === 'success' ? C.greenLight : C.redLight,
            border: `1.5px solid ${type === 'success' ? C.greenBorder : C.redBorder}`,
            color: type === 'success' ? C.greenDark : C.red,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            fontFamily: FONT, cursor: 'pointer', minWidth: 220,
        }}
    >{message}</div>
);

// ─── Field components ─────────────────────────────────────────────────────────
const FieldLabel = ({ children }) => (
    <div style={{
        fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.8, color: C.muted, marginBottom: 6,
    }}>{children}</div>
);

const inputStyle = {
    fontFamily: FONT, fontSize: 13, padding: '8px 10px',
    border: `1.5px solid ${C.border}`, borderRadius: 8,
    outline: 'none', width: '100%', boxSizing: 'border-box',
    color: C.text, background: C.white,
};

const selectStyle = { ...inputStyle, cursor: 'pointer' };

// Multi-select checkboxes for categories / products
const MultiSelectList = ({ items, selected, onToggle, labelFn, valueFn }) => (
    <div style={{
        border: `1.5px solid ${C.border}`, borderRadius: 8,
        maxHeight: 180, overflowY: 'auto', background: C.white,
    }}>
        {items.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 13, color: C.muted, fontFamily: FONT }}>—</div>
        ) : items.map(item => {
            const val = valueFn(item);
            const checked = selected.includes(val);
            return (
                <label key={val} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', cursor: 'pointer',
                    borderBottom: `1px solid ${C.border}`,
                    background: checked ? C.greenLight : 'transparent',
                    fontFamily: FONT, fontSize: 13, color: C.text,
                }}>
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggle(val)}
                        style={{ accentColor: C.green, width: 15, height: 15 }}
                    />
                    {labelFn(item)}
                </label>
            );
        })}
    </div>
);

// Day-of-week chip row
const DayChips = ({ selected, onChange, t }) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {DAYS.map(d => {
            const on = selected.includes(d.value);
            return (
                <button
                    key={d.value}
                    type="button"
                    onClick={() => {
                        onChange(on
                            ? selected.filter(x => x !== d.value)
                            : [...selected, d.value]
                        );
                    }}
                    style={{
                        padding: '6px 11px', borderRadius: 8, border: `1.5px solid ${on ? C.green : C.border}`,
                        background: on ? C.green : C.white, color: on ? C.white : C.muted,
                        fontFamily: FONT, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}
                >{t(d.key)}</button>
            );
        })}
    </div>
);

// Enabled toggle pill
const EnabledToggle = ({ value, onChange }) => (
    <button
        type="button"
        onClick={() => onChange(!value)}
        style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 14px', borderRadius: 999, border: 'none', cursor: 'pointer',
            background: value ? C.green : C.border,
            color: value ? C.white : C.muted,
            fontFamily: FONT, fontSize: 13, fontWeight: 700,
            transition: 'background 0.15s',
        }}
    >
        <span style={{
            width: 14, height: 14, borderRadius: '50%',
            background: value ? C.white : C.white,
            opacity: value ? 1 : 0.5,
        }} />
        {value ? '●' : '○'}
    </button>
);

// ─── Confirm dialog ───────────────────────────────────────────────────────────
const ConfirmDialog = ({ message, onConfirm, onCancel, t }) => (
    <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100,
    }}>
        <div style={{
            background: C.white, borderRadius: 14, padding: 28, maxWidth: 380,
            width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}>
            <p style={{ fontFamily: FONT, fontSize: 14, color: C.text, margin: '0 0 24px', lineHeight: 1.55 }}>
                {message}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                    onClick={onCancel}
                    style={{
                        padding: '9px 20px', borderRadius: 8, border: `1.5px solid ${C.border}`,
                        background: C.white, color: C.text, fontFamily: FONT,
                        fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    }}
                >{t('cancel')}</button>
                <button
                    onClick={onConfirm}
                    style={{
                        padding: '9px 20px', borderRadius: 8, border: 'none',
                        background: C.red, color: C.white, fontFamily: FONT,
                        fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    }}
                >{t('delete')}</button>
            </div>
        </div>
    </div>
);

// ─── Create / Edit modal ──────────────────────────────────────────────────────
const PromotionModal = ({
    initial,
    categories,
    products,
    diningOptions,
    onSave,
    onClose,
    saving,
    t,
}) => {
    const [form, setForm] = useState(() => ({
        ...EMPTY_FORM,
        ...initial,
        rule_config: initial?.rule_config ? { ...initial.rule_config } : {},
        description:  initial?.description || '',
        scope_ids:    Array.isArray(initial?.scope_ids)    ? [...initial.scope_ids]    : [],
        days_of_week: Array.isArray(initial?.days_of_week) ? [...initial.days_of_week] : [],
        start_date:   initial?.start_date ? String(initial.start_date).slice(0, 10) : '',
        end_date:     initial?.end_date   ? String(initial.end_date).slice(0, 10)   : '',
        min_order_value: initial?.min_order_value != null ? String(initial.min_order_value) : '',
        dining_option:   initial?.dining_option || '',
    }));
    const [validationError, setValidationError] = useState('');

    const isEdit = !!initial?.promotion_id;
    const rc = form.rule_config || {};

    // Escape key dismiss
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const set = (key, value) => {
        setValidationError('');
        setForm(f => ({ ...f, [key]: value }));
    };
    const setRc = (key, value) => {
        setValidationError('');
        setForm(f => ({ ...f, rule_config: { ...f.rule_config, [key]: value } }));
    };

    const toggleScopeId = (val) => {
        set('scope_ids', form.scope_ids.includes(val)
            ? form.scope_ids.filter(x => x !== val)
            : [...form.scope_ids, val]
        );
    };

    // Combo items helpers
    const comboItems = Array.isArray(rc.items) ? rc.items : [];
    const addComboItem = () => setRc('items', [...comboItems, { scope_ids: [], qty: 1 }]);
    const removeComboItem = (idx) => setRc('items', comboItems.filter((_, i) => i !== idx));
    const updateComboItem = (idx, key, value) =>
        setRc('items', comboItems.map((it, i) => i === idx ? { ...it, [key]: value } : it));
    const toggleComboItemProduct = (idx, code) => {
        const cur = comboItems[idx]?.scope_ids || [];
        updateComboItem(idx, 'scope_ids', cur.includes(code) ? cur.filter(x => x !== code) : [...cur, code]);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setValidationError('');

        // Rule-specific validation
        const rcNow = form.rule_config || {};
        if (form.rule_type === 'order_percentage' || form.rule_type === 'item_percentage'
            || form.rule_type === 'order_fixed' || form.rule_type === 'item_fixed') {
            if (!(rcNow.value > 0)) {
                setValidationError(t('promo_validation_value_required'));
                return;
            }
        }
        if (form.rule_type === 'bogo') {
            if (!(rcNow.buy_qty >= 1) || !(rcNow.get_qty >= 1)) {
                setValidationError(t('promo_validation_bogo_qty'));
                return;
            }
        }
        if (form.rule_type === 'combo') {
            if (!(rcNow.price > 0)) {
                setValidationError(t('promo_validation_combo_price'));
                return;
            }
            if (!Array.isArray(rcNow.items) || rcNow.items.length === 0) {
                setValidationError(t('promo_validation_combo_items'));
                return;
            }
        }

        const payload = {
            name:             (form.name || '').trim(),
            description:      (form.description || '').trim() || null,
            enabled:          form.enabled,
            application_mode: form.application_mode,
            rule_type:        form.rule_type,
            rule_config:      form.rule_config || {},
            min_order_value:  form.min_order_value !== '' ? parseFloat(form.min_order_value) : null,
            dining_option:    form.dining_option || null,
            scope_type:       form.scope_type,
            scope_ids:        form.scope_type === 'all' ? null : form.scope_ids,
            schedule_type:    form.schedule_type,
            start_date:       form.schedule_type === 'date_range' ? (form.start_date || null) : null,
            end_date:         form.schedule_type === 'date_range' ? (form.end_date || null) : null,
            days_of_week:     form.schedule_type === 'recurring'  ? (form.days_of_week.length ? form.days_of_week : null) : null,
            start_time:       form.schedule_type === 'recurring'  ? (form.start_time || null) : null,
            end_time:         form.schedule_type === 'recurring'  ? (form.end_time || null)   : null,
        };
        onSave(payload);
    };

    const sectionStyle = {
        background: C.bg, borderRadius: 10, padding: '16px 18px', marginBottom: 14,
        border: `1px solid ${C.border}`,
    };
    const rowStyle = { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 };

    return (
        <div
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                zIndex: 1000, overflowY: 'auto', padding: '24px 16px',
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{
                background: C.white, borderRadius: 16, width: '100%', maxWidth: 560,
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)', position: 'relative',
                margin: 'auto',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '20px 24px 16px', borderBottom: `1.5px solid ${C.border}`,
                }}>
                    <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 800, color: C.text, margin: 0 }}>
                        {isEdit ? t('edit_promotion') : t('create_promotion')}
                    </h2>
                    <button
                        onClick={onClose}
                        aria-label={t('cancel')}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: 22, color: C.muted, lineHeight: 1, padding: '2px 6px',
                        }}
                    >×</button>
                </div>

                <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px' }}>
                    {/* ── Basics ── */}
                    <div style={sectionStyle}>
                        <div style={rowStyle}>
                            <FieldLabel>{t('name')}</FieldLabel>
                            <input
                                required
                                value={form.name}
                                onChange={(e) => set('name', e.target.value)}
                                placeholder={t('promotion_name')}
                                style={inputStyle}
                            />
                        </div>
                        <div style={{ ...rowStyle, marginBottom: 0 }}>
                            <FieldLabel>{t('description')}</FieldLabel>
                            <textarea
                                value={form.description}
                                onChange={(e) => set('description', e.target.value)}
                                placeholder={t('promotion_description')}
                                rows={2}
                                style={{ ...inputStyle, resize: 'vertical' }}
                            />
                        </div>
                    </div>

                    {/* ── Rule ── */}
                    <div style={sectionStyle}>
                        <div style={rowStyle}>
                            <FieldLabel>{t('rule_type')}</FieldLabel>
                            <select
                                value={form.rule_type}
                                onChange={(e) => set('rule_type', e.target.value)}
                                style={selectStyle}
                            >
                                {RULE_TYPES.map(rt => (
                                    <option key={rt} value={rt}>{ruleTypeLabel(rt, t)}</option>
                                ))}
                            </select>
                        </div>

                        {/* order_percentage / item_percentage */}
                        {(form.rule_type === 'order_percentage' || form.rule_type === 'item_percentage') && (
                            <div style={rowStyle}>
                                <FieldLabel>{t('discount_percentage')}</FieldLabel>
                                <input
                                    type="number" min={0} max={100} step={0.01}
                                    value={rc.value ?? ''}
                                    onChange={(e) => setRc('value', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                    placeholder={t('placeholder_eg_percent')}
                                    style={inputStyle}
                                />
                            </div>
                        )}

                        {/* order_fixed / item_fixed */}
                        {(form.rule_type === 'order_fixed' || form.rule_type === 'item_fixed') && (
                            <div style={rowStyle}>
                                <FieldLabel>{t('discount_amount')}</FieldLabel>
                                <input
                                    type="number" min={0} step={0.01}
                                    value={rc.value ?? ''}
                                    onChange={(e) => setRc('value', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                    placeholder={t('placeholder_eg_amount')}
                                    style={inputStyle}
                                />
                            </div>
                        )}

                        {/* bogo */}
                        {form.rule_type === 'bogo' && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                                <div style={rowStyle}>
                                    <FieldLabel>{t('buy_quantity')}</FieldLabel>
                                    <input type="number" min={1} value={rc.buy_qty ?? ''}
                                        onChange={(e) => setRc('buy_qty', parseInt(e.target.value, 10) || '')}
                                        placeholder="2" style={inputStyle} />
                                </div>
                                <div style={rowStyle}>
                                    <FieldLabel>{t('get_quantity')}</FieldLabel>
                                    <input type="number" min={1} value={rc.get_qty ?? ''}
                                        onChange={(e) => setRc('get_qty', parseInt(e.target.value, 10) || '')}
                                        placeholder="1" style={inputStyle} />
                                </div>
                                <div style={rowStyle}>
                                    <FieldLabel>{t('get_discount_pct')}</FieldLabel>
                                    <input type="number" min={0} max={100} step={0.01}
                                        value={rc.get_discount_pct ?? ''}
                                        onChange={(e) => setRc('get_discount_pct', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                        placeholder="100" style={inputStyle} />
                                </div>
                            </div>
                        )}

                        {/* combo */}
                        {form.rule_type === 'combo' && (
                            <>
                                <div style={rowStyle}>
                                    <FieldLabel>{t('combo_price')}</FieldLabel>
                                    <input type="number" min={0} step={0.01}
                                        value={rc.price ?? ''}
                                        onChange={(e) => setRc('price', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                        placeholder={t('placeholder_eg_price')} style={inputStyle} />
                                </div>
                                <FieldLabel>{t('combo_items_title')}</FieldLabel>
                                {comboItems.map((ci, idx) => (
                                    <div key={idx} style={{
                                        background: C.faint, borderRadius: 8,
                                        padding: '10px 12px', marginBottom: 8,
                                        border: `1px solid ${C.border}`,
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: C.muted }}>
                                                {t('item')} {idx + 1}
                                            </span>
                                            <button type="button" onClick={() => removeComboItem(idx)}
                                                style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>
                                                ×
                                            </button>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                                            <div style={{ flex: 1 }}>
                                                <FieldLabel>{t('qty')}</FieldLabel>
                                                <input type="number" min={1}
                                                    value={ci.qty ?? 1}
                                                    onChange={(e) => updateComboItem(idx, 'qty', parseInt(e.target.value, 10) || 1)}
                                                    style={{ ...inputStyle, width: 70 }} />
                                            </div>
                                            <div style={{ flex: 3 }}>
                                                <FieldLabel>{t('scope_products')}</FieldLabel>
                                                <div style={{
                                                    border: `1.5px solid ${C.border}`, borderRadius: 8,
                                                    maxHeight: 100, overflowY: 'auto', background: C.white,
                                                }}>
                                                    {products.slice(0, 60).map(p => {
                                                        const cur = ci.scope_ids || [];
                                                        const checked = cur.includes(p.product_code);
                                                        return (
                                                            <label key={p.product_code} style={{
                                                                display: 'flex', alignItems: 'center', gap: 8,
                                                                padding: '5px 10px', cursor: 'pointer',
                                                                background: checked ? C.greenLight : 'transparent',
                                                                fontFamily: FONT, fontSize: 12, color: C.text,
                                                                borderBottom: `1px solid ${C.border}`,
                                                            }}>
                                                                <input type="checkbox" checked={checked}
                                                                    onChange={() => toggleComboItemProduct(idx, p.product_code)}
                                                                    style={{ accentColor: C.green }} />
                                                                {p.name} <span style={{ color: C.muted }}>({p.product_code})</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" onClick={addComboItem}
                                    style={{
                                        padding: '7px 14px', borderRadius: 8, border: `1.5px dashed ${C.green}`,
                                        background: C.greenLight, color: C.greenDark,
                                        fontFamily: FONT, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                    }}>
                                    + {t('add_combo_item')}
                                </button>
                            </>
                        )}
                    </div>

                    {/* ── Scope ── */}
                    <div style={sectionStyle}>
                        <div style={rowStyle}>
                            <FieldLabel>{t('scope_type')}</FieldLabel>
                            <select value={form.scope_type} onChange={(e) => { set('scope_type', e.target.value); set('scope_ids', []); }} style={selectStyle}>
                                <option value="all">{t('scope_all')}</option>
                                <option value="categories">{t('scope_categories')}</option>
                                <option value="products">{t('scope_products')}</option>
                            </select>
                        </div>
                        {form.scope_type === 'categories' && (
                            <div style={rowStyle}>
                                <FieldLabel>{t('scope_categories')}</FieldLabel>
                                {/* scope_ids stores category NAMES (POS basket matches on name) */}
                                <MultiSelectList
                                    items={categories}
                                    selected={form.scope_ids}
                                    onToggle={toggleScopeId}
                                    labelFn={(c) => c.name}
                                    valueFn={(c) => c.name}
                                />
                            </div>
                        )}
                        {form.scope_type === 'products' && (
                            <div style={rowStyle}>
                                <FieldLabel>{t('scope_products')}</FieldLabel>
                                {/* scope_ids stores product_code values (POS basket matches on product_code) */}
                                <MultiSelectList
                                    items={products}
                                    selected={form.scope_ids}
                                    onToggle={toggleScopeId}
                                    labelFn={(p) => `${p.name} (${p.product_code})`}
                                    valueFn={(p) => p.product_code}
                                />
                            </div>
                        )}
                    </div>

                    {/* ── Conditions ── */}
                    <div style={sectionStyle}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div style={rowStyle}>
                                <FieldLabel>{t('min_order_value_label')}</FieldLabel>
                                <input
                                    type="number" min={0} step={0.01}
                                    value={form.min_order_value}
                                    onChange={(e) => set('min_order_value', e.target.value)}
                                    placeholder="0.00"
                                    style={inputStyle}
                                />
                            </div>
                            <div style={rowStyle}>
                                <FieldLabel>{t('dining_option_label')}</FieldLabel>
                                <select value={form.dining_option} onChange={(e) => set('dining_option', e.target.value)} style={selectStyle}>
                                    <option value="">{t('all_dining_options')}</option>
                                    {diningOptions.map(d => (
                                        <option key={d.id} value={d.name}>{d.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* ── Schedule ── */}
                    <div style={sectionStyle}>
                        <div style={rowStyle}>
                            <FieldLabel>{t('schedule_type')}</FieldLabel>
                            <select value={form.schedule_type} onChange={(e) => set('schedule_type', e.target.value)} style={selectStyle}>
                                <option value="always">{t('schedule_always')}</option>
                                <option value="date_range">{t('schedule_date_range')}</option>
                                <option value="recurring">{t('schedule_recurring')}</option>
                            </select>
                        </div>
                        {form.schedule_type === 'date_range' && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div style={rowStyle}>
                                    <FieldLabel>{t('start_date')}</FieldLabel>
                                    <input type="date" value={form.start_date}
                                        onChange={(e) => set('start_date', e.target.value)}
                                        style={inputStyle} />
                                </div>
                                <div style={rowStyle}>
                                    <FieldLabel>{t('end_date')}</FieldLabel>
                                    <input type="date" value={form.end_date}
                                        onChange={(e) => set('end_date', e.target.value)}
                                        style={inputStyle} />
                                </div>
                            </div>
                        )}
                        {form.schedule_type === 'recurring' && (
                            <>
                                <div style={rowStyle}>
                                    <FieldLabel>{t('days_of_week')}</FieldLabel>
                                    <DayChips
                                        selected={form.days_of_week}
                                        onChange={(v) => set('days_of_week', v)}
                                        t={t}
                                    />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div style={rowStyle}>
                                        <FieldLabel>{t('start_time')}</FieldLabel>
                                        <input type="time" value={form.start_time}
                                            onChange={(e) => set('start_time', e.target.value)}
                                            style={inputStyle} />
                                    </div>
                                    <div style={rowStyle}>
                                        <FieldLabel>{t('end_time')}</FieldLabel>
                                        <input type="time" value={form.end_time}
                                            onChange={(e) => set('end_time', e.target.value)}
                                            style={inputStyle} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* ── Settings ── */}
                    <div style={sectionStyle}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div style={rowStyle}>
                                <FieldLabel>{t('application_mode')}</FieldLabel>
                                <select value={form.application_mode} onChange={(e) => set('application_mode', e.target.value)} style={selectStyle}>
                                    <option value="automatic">{t('app_mode_automatic')}</option>
                                    <option value="manual">{t('app_mode_manual')}</option>
                                </select>
                            </div>
                            <div style={rowStyle}>
                                <FieldLabel>{t('promotion_enabled')}</FieldLabel>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 4 }}>
                                    <EnabledToggle value={form.enabled} onChange={(v) => set('enabled', v)} />
                                    <span style={{ fontFamily: FONT, fontSize: 13, color: form.enabled ? C.greenDark : C.muted }}>
                                        {form.enabled ? t('yes') : t('no')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Footer ── */}
                    {validationError && (
                        <div style={{
                            fontFamily: FONT, fontSize: 12, color: C.red,
                            background: C.redLight, border: `1px solid ${C.red}`,
                            borderRadius: 7, padding: '8px 12px', marginBottom: 10,
                        }}>{validationError}</div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                padding: '10px 22px', borderRadius: 9, border: `1.5px solid ${C.border}`,
                                background: C.white, color: C.text,
                                fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                            }}
                        >{t('cancel')}</button>
                        <button
                            type="submit"
                            disabled={saving}
                            style={{
                                padding: '10px 22px', borderRadius: 9, border: 'none',
                                background: C.green, color: C.white,
                                fontFamily: FONT, fontSize: 13, fontWeight: 700,
                                cursor: saving ? 'not-allowed' : 'pointer',
                                opacity: saving ? 0.7 : 1,
                            }}
                        >{saving ? `${t('save')}…` : t('save')}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ─── Badge components ─────────────────────────────────────────────────────────
const ModeBadge = ({ mode, t }) => (
    <span style={{
        padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700,
        fontFamily: FONT,
        background: mode === 'automatic' ? C.blueLight : C.amberLight,
        color:      mode === 'automatic' ? C.blue      : C.amber,
        border: `1px solid ${mode === 'automatic' ? C.blueBorder : C.amberBorder}`,
    }}>
        {mode === 'automatic' ? t('mode_automatic') : t('mode_manual')}
    </span>
);

const EnabledBadge = ({ enabled }) => (
    <span style={{
        display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
        background: enabled ? C.green : C.border,
        flexShrink: 0,
    }} />
);

// ─── Inline enable toggle (auto-saves on flip) ────────────────────────────────
const InlineToggle = ({ promo, onFlip, t }) => {
    const [busy, setBusy] = useState(false);
    const handleClick = async (e) => {
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
            await onFlip(promo, !promo.enabled);
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            onClick={handleClick}
            disabled={busy}
            title={promo.enabled ? t('disable') : t('enable')}
            style={{
                width: 36, height: 20, borderRadius: 10, border: 'none',
                background: promo.enabled ? C.green : C.border,
                cursor: busy ? 'not-allowed' : 'pointer',
                position: 'relative', flexShrink: 0,
                transition: 'background 0.15s',
                opacity: busy ? 0.6 : 1,
            }}
        >
            <span style={{
                position: 'absolute', top: 2,
                left: promo.enabled ? 18 : 2,
                width: 16, height: 16, borderRadius: '50%',
                background: C.white,
                transition: 'left 0.15s',
            }} />
        </button>
    );
};

// ─── Empty state ──────────────────────────────────────────────────────────────
const PromotionsEmpty = ({ onCreate, t }) => (
    <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center', padding: '64px 24px',
    }}>
        <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: `linear-gradient(135deg, ${C.green}, ${C.greenDark})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 20, fontSize: 28,
        }}>
            🏷️
        </div>
        <h3 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 800, color: C.text, margin: '0 0 8px' }}>
            {t('no_promotions_yet')}
        </h3>
        <p style={{ fontFamily: FONT, fontSize: 14, color: C.muted, margin: '0 0 24px', lineHeight: 1.6, maxWidth: 360 }}>
            {t('no_promotions_desc')}
        </p>
        <button
            onClick={onCreate}
            style={{
                padding: '10px 24px', borderRadius: 9, border: 'none',
                background: C.green, color: C.white,
                fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
        >{t('create_promotion')}</button>
    </div>
);

// ─── Main PromotionsTab ───────────────────────────────────────────────────────
const PromotionsTab = () => {
    const { t } = useLanguage();
    const isMobile = useIsMobile();

    const [promotions, setPromotions] = useState([]);
    const [loading, setLoading]       = useState(true);
    const [loadError, setLoadError]   = useState(false);

    // Reference data
    const [categories, setCategories]     = useState([]);
    const [products, setProducts]         = useState([]);
    const [diningOptions, setDiningOptions] = useState([]);

    // Modal & confirm
    const [modalOpen, setModalOpen]     = useState(false);
    const [editing, setEditing]         = useState(null);   // null = create
    const [saving, setSaving]           = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // promo to delete

    // Toasts
    const [toasts, setToasts] = useState([]);
    const addToast = useCallback((type, message) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, type, message }]);
        setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 3500);
    }, []);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const [promos, cats, prods, dops] = await Promise.all([
                promotionsService.getAll(),
                categoryService.getAll(),
                productService.getAllProducts(),
                diningOptionService.getAll(),
            ]);
            setPromotions(Array.isArray(promos) ? promos : []);
            setCategories(Array.isArray(cats)  ? cats  : []);
            setProducts(Array.isArray(prods)   ? prods : []);
            setDiningOptions(Array.isArray(dops) ? dops : []);
        } catch {
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const openCreate = () => { setEditing(null); setModalOpen(true); };
    const openEdit   = (promo) => { setEditing(promo); setModalOpen(true); };
    const closeModal = () => { setModalOpen(false); setEditing(null); };

    const handleSave = async (payload) => {
        setSaving(true);
        try {
            if (editing?.promotion_id) {
                await promotionsService.update(editing.promotion_id, payload);
            } else {
                await promotionsService.create(payload);
            }
            addToast('success', t('promotion_saved'));
            closeModal();
            await loadAll();
        } catch {
            addToast('error', t('failed_to_save_promotion'));
        } finally {
            setSaving(false);
        }
    };

    const handleFlipEnabled = async (promo, newEnabled) => {
        try {
            await promotionsService.update(promo.promotion_id, toApiPayload(promo, { enabled: newEnabled }));
            setPromotions(prev => prev.map(p =>
                p.promotion_id === promo.promotion_id ? { ...p, enabled: newEnabled } : p
            ));
        } catch {
            addToast('error', t('failed_to_save_promotion'));
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await promotionsService.delete(deleteTarget.promotion_id);
            addToast('success', t('promotion_deleted'));
            setDeleteTarget(null);
            await loadAll();
        } catch {
            addToast('error', t('failed_to_delete_promotion'));
            setDeleteTarget(null);
        }
    };

    // ── Render ──
    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 0', fontFamily: FONT, color: C.muted, fontSize: 14 }}>
                {t('loading')}
            </div>
        );
    }

    if (loadError) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <p style={{ fontFamily: FONT, color: C.red, fontSize: 14, marginBottom: 16 }}>
                    {t('failed_to_load_promotions')}
                </p>
                <button onClick={loadAll} style={{
                    padding: '9px 20px', borderRadius: 8, border: `1.5px solid ${C.border}`,
                    background: C.white, color: C.text, fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}>{t('retry')}</button>
            </div>
        );
    }

    const headerStyle = {
        fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.8, color: C.muted,
        padding: '8px 12px', borderBottom: `1.5px solid ${C.border}`,
    };

    const cellStyle = {
        fontFamily: FONT, fontSize: 13, color: C.text,
        padding: '10px 12px', borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', gap: 8,
    };

    const cols = isMobile
        ? '1fr auto auto'
        : '2fr 1.3fr 1.3fr minmax(120px, 1fr) 90px 150px';

    return (
        <>
            {/* Toast layer */}
            <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
                {toasts.map(toast => (
                    <div key={toast.id} style={{ pointerEvents: 'auto' }}>
                        <Toast message={toast.message} type={toast.type} onDismiss={() => setToasts(p => p.filter(x => x.id !== toast.id))} />
                    </div>
                ))}
            </div>

            {/* Page header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 800, color: C.text, margin: '0 0 4px' }}>
                        {t('promotions')}
                    </h2>
                    <p style={{ fontFamily: FONT, fontSize: 13, color: C.muted, margin: 0 }}>
                        {t('promotions_desc')}
                    </p>
                </div>
                <button
                    onClick={openCreate}
                    style={{
                        padding: '10px 20px', borderRadius: 9, border: 'none',
                        background: C.green, color: C.white,
                        fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        flexShrink: 0,
                    }}
                >{t('new_promotion')}</button>
            </div>

            {/* Empty state */}
            {promotions.length === 0 ? (
                <div style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12 }}>
                    <PromotionsEmpty onCreate={openCreate} t={t} />
                </div>
            ) : (
                <div style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                    {isMobile ? (
                        /* Mobile card list */
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {promotions.map(promo => (
                                <div key={promo.promotion_id} style={{
                                    padding: '14px 16px', borderBottom: `1px solid ${C.border}`,
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                <EnabledBadge enabled={promo.enabled} />
                                                <span style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: C.text }}>{promo.name}</span>
                                            </div>
                                            <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>
                                                {ruleTypeLabel(promo.rule_type, t)} · {scheduleLabel(promo, t)}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <InlineToggle promo={promo} onFlip={handleFlipEnabled} t={t} />
                                            <button onClick={() => openEdit(promo)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.green, fontSize: 13, fontWeight: 700, fontFamily: FONT }}>{t('edit')}</button>
                                            <button onClick={() => setDeleteTarget(promo)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.red, fontSize: 13, fontWeight: 700, fontFamily: FONT }}>{t('delete')}</button>
                                        </div>
                                    </div>
                                    <ModeBadge mode={promo.application_mode} t={t} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* Desktop grid */
                        <div style={{ display: 'grid', gridTemplateColumns: cols }}>
                            <div style={headerStyle}>{t('name')}</div>
                            <div style={headerStyle}>{t('rule_type')}</div>
                            <div style={headerStyle}>{t('schedule_type')}</div>
                            <div style={headerStyle}>{t('application_mode')}</div>
                            <div style={headerStyle}>{t('promotion_enabled')}</div>
                            <div style={headerStyle}></div>

                            {promotions.map(promo => (
                                <React.Fragment key={promo.promotion_id}>
                                    <div style={{ ...cellStyle, fontWeight: 600 }}>
                                        <EnabledBadge enabled={promo.enabled} />
                                        {promo.name}
                                    </div>
                                    <div style={cellStyle}>
                                        <span style={{ fontFamily: FONT, fontSize: 12, padding: '2px 8px', borderRadius: 5, background: C.bg, border: `1px solid ${C.border}`, color: C.muted, fontWeight: 600 }}>
                                            {ruleTypeLabel(promo.rule_type, t)}
                                        </span>
                                    </div>
                                    <div style={{ ...cellStyle, color: C.muted, fontSize: 12 }}>
                                        {scheduleLabel(promo, t)}
                                    </div>
                                    <div style={cellStyle}>
                                        <ModeBadge mode={promo.application_mode} t={t} />
                                    </div>
                                    <div style={cellStyle}>
                                        <InlineToggle promo={promo} onFlip={handleFlipEnabled} t={t} />
                                    </div>
                                    <div style={{ ...cellStyle, justifyContent: 'flex-end', gap: 8 }}>
                                        <button
                                            onClick={() => openEdit(promo)}
                                            title={t('edit')}
                                            style={{
                                                padding: '5px 12px', borderRadius: 7, border: `1.5px solid ${C.green}`,
                                                background: C.greenLight, color: C.greenDark,
                                                fontFamily: FONT, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                            }}
                                        >{t('edit')}</button>
                                        <button
                                            onClick={() => setDeleteTarget(promo)}
                                            title={t('delete')}
                                            style={{
                                                padding: '5px 12px', borderRadius: 7, border: `1.5px solid ${C.redBorder}`,
                                                background: C.redLight, color: C.red,
                                                fontFamily: FONT, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                            }}
                                        >{t('delete')}</button>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Create/Edit modal */}
            {modalOpen && (
                <PromotionModal
                    initial={editing}
                    categories={categories}
                    products={products}
                    diningOptions={diningOptions}
                    onSave={handleSave}
                    onClose={closeModal}
                    saving={saving}
                    t={t}
                />
            )}

            {/* Delete confirm */}
            {deleteTarget && (
                <ConfirmDialog
                    message={t('confirm_delete_promotion')}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    t={t}
                />
            )}
        </>
    );
};

export default PromotionsTab;
